import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    TableRow,
    TableCell,
    Typography,
    Box,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import { Edit, DragIndicator, ContentCut, Redo, MoreVert } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { isAddedToday } from '../../utils/dateUtils';
import { Plant, Space } from '../../types/shared';

interface DraggablePlantRowProps {
    plant: Plant;
    space: Space;
    onEditPlant: (plant: Plant) => void;
    onRemovePlant?: (plant: Plant) => void;
    onTransplant?: (plant: Plant) => void;
    onImageClick?: (imageUrl: string) => void;
}

const DraggablePlantRow: React.FC<DraggablePlantRowProps> = ({ plant, space, onEditPlant, onRemovePlant, onTransplant, onImageClick }) => {
    const { t } = useTranslation();
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id: plant.id,
        data: {
            type: 'plant',
            plant,
            spaceId: plant.spaceId,
        },
    });

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        setAnchorEl(null);
    };

    const handleEdit = (e: React.MouseEvent) => {
        handleMenuClose(e);
        onEditPlant(plant);
    };



    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <TableRow
            ref={setNodeRef}
            style={style}
            {...attributes}
            sx={{
                bgcolor: isDragging ? 'action.hover' : 'inherit',
                // Hide border of last row
                '&:last-child td, &:last-child th': { border: 0 }
            }}
        >
            {/* Drag Handle */}
            <TableCell sx={{ p: 1, width: 40 }}>
                <Box
                    {...listeners}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: 'text.secondary',
                        cursor: 'grab',
                        touchAction: 'none',
                    }}
                >
                    <DragIndicator fontSize="small" />
                </Box>
            </TableCell>

            {/* Image */}
            <TableCell sx={{ p: 1, width: 60 }}>
                {plant.imageUrl ? (
                    <Box
                        component="img"
                        src={plant.imageUrl}
                        alt={plant.name}
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1,
                            objectFit: 'cover',
                            cursor: 'pointer'
                        }}
                        onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            if (onImageClick) onImageClick(plant.imageUrl!);
                        }}
                    />
                ) : (
                    <Box
                        component="img"
                        src="/icons/generic-seedling.webp"
                        alt="Seedling"
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1,
                            objectFit: 'contain',
                            bgcolor: 'white'
                        }}
                    />
                )}
            </TableCell>

            {/* Name & Details */}
            <TableCell sx={{ p: 1 }}>
                <Typography variant="body2" component="h3" className="serif-text" sx={{ fontWeight: 600, textAlign: 'left' }}>
                    {plant.name}
                    {plant.variety && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic', mt: -0.5, fontWeight: 400, textAlign: 'left' }}>
                            {plant.variety}
                        </Typography>
                    )}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                    {(plant.count || 1) > 1 && (
                        <Chip
                            label={`×${plant.count}`}
                            size="small"
                            variant="outlined"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                        />
                    )}
                    {plant.stage === 'Seedling' && (
                        <Chip
                            label={t('plants.seedling_badge')}
                            size="small"
                            variant="outlined"
                            sx={{ height: 18, fontSize: '0.65rem', borderColor: 'primary.main', color: 'primary.main' }}
                        />
                    )}
                    {isAddedToday(plant.createdAt) && (
                        <Chip
                            label={t('plants.new_badge')}
                            size="small"
                            color="primary"
                            variant="filled"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                        />
                    )}
                </Box>
            </TableCell>

            {/* Actions */}
            <TableCell align="right" sx={{ p: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    {plant.stage === 'Seedling' && (
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (onTransplant) onTransplant(plant);
                            }}
                            sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                            title={t('plants.transplant_tooltip')}
                        >
                            <Redo fontSize="small" />
                        </IconButton>
                    )}
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (onRemovePlant) onRemovePlant(plant);
                        }}
                        sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                    >
                        <ContentCut fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={handleMenuClick}
                        sx={{ color: 'text.secondary' }}
                    >
                        <MoreVert fontSize="small" />
                    </IconButton>
                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={() => setAnchorEl(null)}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <MenuItem onClick={handleEdit}>
                            <ListItemIcon>
                                <Edit fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>{t('plants.edit_menu')}</ListItemText>
                        </MenuItem>
                    </Menu>
                </Box>
            </TableCell>
        </TableRow>
    );
};

export default DraggablePlantRow;
